import type { InternalNote } from "@nlc/shared-types";

import { USER_ROLES } from "@nlc/shared-types";
import { useNavigate } from "@tanstack/react-router";
import { File, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { NoteFormValues } from "@/features/projects/project/internalNotes/types";

import { Button } from "@/components/button";
import { Link } from "@/components/link";
import { env } from "@/config/env";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";

import type { CreateNoteHandle } from "./components/CreateNote";
import type { UpdateNoteHandle } from "./components/UpdateNote";

import { CreateNote } from "./components/CreateNote";
import Notes from "./components/NoteList";
import { UpdateNote } from "./components/UpdateNote";
import { useCreateRequestInternalNote } from "./hooks/useCreateRequestInternalNote";
import { useUpdateRequestInternalNote } from "./hooks/useUpdateRequestInternalNote";

type View = "add" | "notes" | "update";

function InternalNotesPanel({
  isOpen,
  onClose,
  projectId,
  requestId,
}: {
  isOpen: boolean;
  onClose: () => void;
  projectId?: number;
  requestId?: number;
}) {
  const [view, setView] = useState<View>("notes");
  const [editingNote, setEditingNote] = useState<InternalNote | null>(null);

  const { hasRole } = useRoleAccess();
  const canAddInternalNotes = hasRole([
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.TEAM_LEADER,
    USER_ROLES.CUSTOMER_SUCCESS,
  ]);

  const createRef = useRef<CreateNoteHandle>(null);
  const updateRef = useRef<UpdateNoteHandle>(null);

  const createNote = useCreateRequestInternalNote(Number(requestId));
  const updateNote = useUpdateRequestInternalNote(Number(requestId));

  async function handleSaveEdit(
    noteId: number,
    values: NoteFormValues,
  ): Promise<void> {
    await updateNote.mutateAsync({
      body: { description: values.description, name: values.name },
      noteId,
    });
  }

  function handleEdit(note: InternalNote) {
    setEditingNote(note);
    setView("update");
  }

  function handleSubmit() {
    if (view === "add") createRef.current?.submitForm();
    if (view === "update") updateRef.current?.submitForm();
  }

  const isSaving =
    updateNote.isPending || (view === "add" && createNote.isPending);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-50 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-96 bg-bg-primary shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between pl-6 pr-4 pb-4 pt-4 border-b border-border-primary">
            <h2 className="text-xl font-semibold text-text-primary">
              Request notes
            </h2>
            <button
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={onClose}
              type="button"
            >
              <XIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {view === "notes" && (
              <Notes
                onEdit={handleEdit}
                projectId={Number(projectId)}
                requestId={Number(requestId)}
              />
            )}
            {view === "add" && (
              <CreateNote
                createNote={createNote}
                onSuccess={() => setView("notes")}
                ref={createRef}
                requestId={Number(requestId)}
              />
            )}
            {view === "update" && (
              <UpdateNote
                note={editingNote}
                onSave={handleSaveEdit}
                onSuccess={() => setView("notes")}
                ref={updateRef}
              />
            )}
          </div>

          {view === "notes" ? (
            <div className="p-6 border-t border-border-primary space-y-3">
              <Link
                className="w-full"
                href={`${env.VITE_BASE_URL}/projects/${projectId}/internal-notes`}
                target="_blank"
              >
                <Button className="w-full" variant="secondary">
                  See Account&apos;s internal notes
                </Button>
              </Link>
              <Button
                className="w-full"
                isDisabled={!canAddInternalNotes}
                onPress={() => setView("add")}
                variant="primary"
              >
                Add new request note
              </Button>
            </div>
          ) : (
            <div className="p-6 border-t border-border-primary space-y-3">
              <Button
                className="w-full"
                onPress={() => {
                  setView("notes");
                  setEditingNote(null);
                }}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                className="w-full"
                isDisabled={!canAddInternalNotes || isSaving}
                isLoading={isSaving}
                onPress={handleSubmit}
                variant="primary"
              >
                {view === "update" ? "Save note" : "Add note"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function InternalNotesWidget({
  initialOpen,
  projectId,
  requestId,
}: {
  initialOpen?: boolean;
  projectId?: number;
  requestId?: number;
}) {
  const [isOpen, setIsOpen] = useState(initialOpen ?? false);
  const [hasOpened, setHasOpened] = useState(initialOpen ?? false);
  const navigate = useNavigate();

  useEffect(() => {
    if (initialOpen) {
      navigate({
        replace: true,
        // @ts-expect-error -- navigate is not route-scoped here; removing the param is safe
        search: (prev) => ({ ...prev, "internal-notes": undefined }),
      });
    }
  }, [initialOpen, navigate]);

  const togglePopup = () => {
    setIsOpen((prev) => {
      if (!prev) setHasOpened(true);

      return !prev;
    });
  };

  return (
    <>
      <button
        className="fixed top-55 right-5 z-40 bg-bg-primary border border-border-primary rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-bg-active cursor-pointer"
        data-cy="internal-notes"
        onClick={togglePopup}
        type="button"
      >
        <File className="w-5 h-5 text-gray-600" />
      </button>

      {hasOpened && (
        <InternalNotesPanel
          isOpen={isOpen}
          onClose={togglePopup}
          projectId={projectId}
          requestId={requestId}
        />
      )}
    </>
  );
}
